import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { companyService } from "~/services/contabilidad/companyService";
import type { CompanyFormData } from "~/types/contabilidad";

export const COMPANIES_QUERY_KEY = ["companies"];

export function useCompanies() {
  return useQuery({
    queryKey: COMPANIES_QUERY_KEY,
    queryFn: companyService.getAll,
  });
}

export function useCompany(id: string) {
  return useQuery({
    queryKey: [...COMPANIES_QUERY_KEY, id],
    queryFn: () => companyService.getById(id),
    enabled: !!id,
  });
}

export function useSearchCompanies(query: string) {
  return useQuery({
    queryKey: [...COMPANIES_QUERY_KEY, "search", query],
    queryFn: () => companyService.search(query),
    enabled: query.length > 0,
  });
}

export function useCreateCompany() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CompanyFormData) => companyService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: COMPANIES_QUERY_KEY });
      toast.success("Empresa creada exitosamente");
    },
    onError: (error: Error) => {
      toast.error(`Error al crear empresa: ${error.message}`);
    },
  });
}

export function useUpdateCompany() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: Partial<CompanyFormData>;
    }) => companyService.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: COMPANIES_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: [...COMPANIES_QUERY_KEY, id] });
      toast.success("Empresa actualizada exitosamente");
    },
    onError: (error: Error) => {
      toast.error(`Error al actualizar empresa: ${error.message}`);
    },
  });
}

export function useDeleteCompany() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => companyService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: COMPANIES_QUERY_KEY });
      toast.success("Empresa eliminada exitosamente");
    },
    onError: (error: Error) => {
      toast.error(`Error al eliminar empresa: ${error.message}`);
    },
  });
}
